import Modal from "aldoo-ra/Modal"
import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import PubSub from "pubsub-js"
import Button from "aldoo-ra/Button"
import DropDown from "aldoo-ra/Dropdown"
import Input from "aldoo-ra/Input"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import Tooltip from "aldoo-ra/Tooltip"
import Spinner from "aldoo-ra/Spinner"
import { FreezeTranslation } from "./api"
import SnowflakeIcon, { getFreezeState, getFreezeTitle } from "./snowflake-icon"
//Editors
import TextItemEditor from "./item-editors/text"
import ImageItemEditor from "./item-editors/image"
import VideoItemEditor from "./item-editors/video"
import FileItemEditor from "./item-editors/file"
import ObjectItemEditor from "./item-editors/object"

const typeToComponentMap = {
  text: TextItemEditor,
  image: ImageItemEditor,
  video: VideoItemEditor,
  file: FileItemEditor,
  object: ObjectItemEditor,
}

// Resolve the delta between the original and updated translations
const resolveTranslationDelta = (original, updated, controller, itemType) => {
  const delta = {}
  for (const lang in updated) {
    // Apply post-formatting to updated content to match server storage format
    const updatedContentFormatted = controller.applyPostFormatters(
      updated[lang].content,
      itemType
    )

    if (
      JSON.stringify(updatedContentFormatted) !==
      JSON.stringify(original[lang]?.content)
    ) {
      if (!delta[lang]) delta[lang] = original[lang] || {}
      delta[lang].content = updated[lang].content
    }
  }
  return delta
}

export default function LocalizationItemDialog({
  isOpen,
  onClose,
  onSave,
  item,
  controller,
  parentObject,
  zIndex,
  initialOverrides,
}) {
  const languages = controller.languages
  const [selectedLang, setSelectedLang] = useState(null) // Default selected language
  const [translations, setTranslations] = useState({})
  const [searchTerm, setSearchTerm] = useState("") // Search term for filtering languages
  const [filterOption, setFilterOption] = useState("all") // "all" by default
  const textAreaRef = useRef(null) // Ref to focus textarea
  const [pendingLanguages, setPendingLanguages] = useState([]) // Languages that contain unsaved changes
  const [filteredLanguages, setFilteredLanguages] = useState([]) // Filtered languages
  const [primaryLanguage, setPrimaryLanguage] = useState(
    controller.primaryLanguage
  ) // Primary
  const [EditorComponent, setEditorComponent] = useState(null)
  const [freezeStates, setFreezeStates] = useState({}) // Track freeze states locally
  const [pendingFreezeChanges, setPendingFreezeChanges] = useState({}) // Track pending freeze changes
  const [hasPendingFreezeChanges, setHasPendingFreezeChanges] = useState(false) // Flag for pending freeze changes

  //Select the primary language by default
  useEffect(() => {
    setSelectedLang(primaryLanguage)
  }, [primaryLanguage])

  // Ensure freeze states are properly initialized when selected language changes
  useEffect(() => {
    if (!selectedLang || !translations || !translations[selectedLang]) {
      return
    }
    
    setFreezeStates(prev => {
      // Always refresh the freeze state for the selected language to ensure sync with server
      const currentTranslation = translations[selectedLang]
      const serverFrozenArray = currentTranslation.frozen || []
      
      
      return {
        ...prev,
        [selectedLang]: serverFrozenArray
      }
    })
  }, [selectedLang, translations])

  useEffect(() => {
    if (!controller.primaryLanguage) return
    setPrimaryLanguage(controller.primaryLanguage)
  }, [controller.primaryLanguage])

  // Listen for per-field A+ completions and update translations state so language switches show fresh data
  useEffect(() => {
    const token = PubSub.subscribe('localization.auto-translate-completed', (_msg, data) => {
      if (data.localizationKey === item?.key && data.translations) {
        setTranslations(data.translations)
      }
    })
    return () => PubSub.unsubscribe(token)
  }, [item?.key])

  //reset the pending on open
  useEffect(() => {
    if (isOpen) {
      setPendingLanguages([])
      setPendingFreezeChanges({})
      setHasPendingFreezeChanges(false)
      
      // Initialize freeze states with current server state on open - reset completely
      if (item?.translations) {
        const initialFreezeStates = {}
        for (const lang in item.translations) {
          // Always initialize with server state, including empty arrays
          initialFreezeStates[lang] = item.translations[lang]?.frozen || []
        }
        setFreezeStates(initialFreezeStates)
      } else {
        // No translations yet, reset freeze states
        setFreezeStates({})
      }
    } else {
      // Reset all state when dialog closes to prevent stale data
      setSelectedLang(primaryLanguage)
      setTranslations({})
      setFreezeStates({})
      setPendingLanguages([])
      setPendingFreezeChanges({})
      setHasPendingFreezeChanges(false)
      setSearchTerm("")
      setFilterOption("all")
      setEditorComponent(null)
    }
  }, [isOpen, item, primaryLanguage])

  useEffect(() => {
    if (!item || !controller) return

    // Store the current item reference to validate against later
    const currentItemKey = item.key

    // If translations are already loaded for this item, skip reloading
    // This prevents overwriting local updates with stale prop data
    if (translations && Object.keys(translations).length > 0 && translations[Object.keys(translations)[0]]?.content) {
      return
    }

    // Reset all state when a new item is loaded
    setPendingLanguages([]) // Clear pending changes
    setPendingFreezeChanges({}) // Clear pending freeze changes
    setHasPendingFreezeChanges(false) // Clear freeze change flag
    setSearchTerm("") // Clear search
    setFilterOption("all") // Reset filter

    // Function to load and format translations
    const loadTranslations = async () => {
      const itemData = item

      // Initialize freeze states from the item
      const initialFreezeStates = {}
      if (itemData?.translations) {
        for (const lang in itemData.translations) {
          if (itemData.translations[lang]) {
            initialFreezeStates[lang] = itemData.translations[lang]?.frozen || []
          }
        }
      }
      setFreezeStates(initialFreezeStates)

      // Apply pre-formatting to convert storage format to UI format
      const formattedTranslations = JSON.parse(JSON.stringify(itemData.translations || {}))

      // Set translations immediately with unformatted data to ensure UI loads
      setTranslations(formattedTranslations)

      // Process formatting asynchronously but with validation
      const processFormatting = async () => {
        try {
          const updatedTranslations = JSON.parse(JSON.stringify(itemData.translations || {}))

          for (const lang in updatedTranslations) {
            // Check if we're still working with the same item
            if (item.key !== currentItemKey) {
              return
            }

            updatedTranslations[lang].content = controller.applyPreFormatters(
              updatedTranslations[lang].content,
              itemData.type
            )
          }

          // Final validation before setting formatted state
          if (item.key === currentItemKey) {
            setTranslations(updatedTranslations)
          }
        } catch (error) {
          console.error('Error formatting translations:', error)
          // Keep the unformatted translations that were already set
        }
      }

      // Only apply formatting if needed (avoid unnecessary async operations)
      if (controller.applyPreFormatters && itemData.type) {
        await processFormatting()
      }

      // Apply initial overrides — seeds translations state without touching item.translations
      // so the delta calculation correctly detects them as changes.
      if (initialOverrides && item.key === currentItemKey) {
        setTranslations(prev => {
          const next = { ...prev }
          for (const [lang, content] of Object.entries(initialOverrides)) {
            next[lang] = { ...(next[lang] || {}), content }
          }
          return next
        })
        const pendingFromOverrides = Object.entries(initialOverrides)
          .filter(([lang, content]) => JSON.stringify(content) !== JSON.stringify(itemData.translations?.[lang]?.content))
          .map(([lang]) => lang)
        if (pendingFromOverrides.length > 0) {
          setPendingLanguages(prev => [...new Set([...prev, ...pendingFromOverrides])])
        }
      }

      //resolve the editor component
      // Check if this is a text type with structured object data
      const hasObjectStructure = itemData?.translations &&
        Object.values(itemData.translations).some(translation =>
          Array.isArray(translation.content) &&
          translation.content.length > 0 &&
          typeof translation.content[0] === 'object' &&
          translation.content[0].hasOwnProperty('key') &&
          translation.content[0].hasOwnProperty('value')
        )

      // Route text types with object structure to ObjectItemEditor
      if (itemData.type === 'text' && hasObjectStructure) {
        setEditorComponent(() => ObjectItemEditor)
      } else if (typeToComponentMap[itemData.type]) {
        setEditorComponent(() => typeToComponentMap[itemData.type])
      }
    }

    loadTranslations()
  }, [item, controller])

  //Apply default value if it exists
  const handleLanguageSelect = (languageCode) => {
    setSelectedLang(languageCode)
    // Set the auto translate option to the selected language
    controller.autoTranslateOptions.find(
      (item) => item.value === "key"
    ).name = `Translate in ${
      languages.find((lang) => lang.code === languageCode).name
    }`
  }

  const handleContentChange = useCallback((value, targetLanguage) => {
    const lang = targetLanguage || selectedLang

    // Always update translations state first
    setTranslations(prev => {
      const updated = {
        ...prev,
        [lang]: {
          ...prev[lang],
          content: value,
        },
      }
      return updated
    })

    // Apply post-formatting to value to match server storage format for comparison
    const formattedValue = controller.applyPostFormatters(value, item?.type)
    const originalContent = item?.translations[lang]?.content

    const isEqual = JSON.stringify(formattedValue) === JSON.stringify(originalContent)

    // Helper function to check if content is effectively empty
    const isEffectivelyEmpty = (content) => {
      if (!content || content === "") return true
      if (Array.isArray(content)) {
        if (content.length === 0) return true
        // Check if array contains only empty key-value pairs
        return content.every(item => 
          !item || 
          (typeof item === 'object' && (!item.key || item.key === "") && (!item.value || item.value === ""))
        )
      }
      return false
    }

    // Compare using formatted values, with special handling for empty content
    const formattedEmpty = isEffectivelyEmpty(formattedValue)
    const originalEmpty = isEffectivelyEmpty(originalContent)

    if (
      JSON.stringify(formattedValue) === JSON.stringify(originalContent) ||
      (formattedEmpty && originalEmpty)
    ) {
      setPendingLanguages(prev => prev.filter(l => l !== lang))
      return
    }

    // Add the language to the pending languages if it doesn't exist
    setPendingLanguages(prev => prev.includes(lang) ? prev : [...prev, lang])
  }, [selectedLang, item, pendingLanguages, controller])

  const checkForBraces = (content) => {
    // The content is not an array, so no need to check
    if (!Array.isArray(content)) return false

    // Check if a key contains braces
    const problematicKeys = content.filter((item) => {
      return item.key && (item.key.includes("{") || item.key.includes("}"))
    })

    if (problematicKeys.length > 0) {
      const keysList = problematicKeys.map((item) => item.key).join(", ")
      return showPromptDialog({
        title: "Error",
        showNoBtn: false,
        yesLabel: "OK",
        message: `The content contains braces '{' or '}' in the following keys: ${keysList}. Please remove them before saving.`,
      })
    }
  }

  const handleSave = async () => {
    if (!item) return

    // Store the current item key to validate against later
    const currentItemKey = item.key

    const delta = resolveTranslationDelta(item.translations, translations, controller, item.type)
    
    //for all languages in the delta, postFormat the content
    // If there are no changes, return

    for (const lang in delta) {
      //check if we have braces in the keys
      if (checkForBraces(delta[lang].content)) {
        return
      }
      delta[lang].content = controller.applyPostFormatters(
        delta[lang].content,
        item.type
      )
    }

    try {
      // Save the item using the controller
      await controller.saveLocalizationItem({
        key: item.key,
        translations: delta,
      })

      // Validate we're still working with the same item after async operation
      if (item.key !== currentItemKey) {
        console.warn('Item changed during save operation, skipping state update')
        return
      }

      // Save pending freeze changes if any
      if (hasPendingFreezeChanges) {
        await savePendingFreezeChanges()
      }

      // Validate again after freeze changes
      if (item.key !== currentItemKey) {
        console.warn('Item changed during freeze save, skipping state update')
        return
      }

      //get the latest item version from the server
      const updatedItem = await controller.getLocalizationKey(item.key)

      // Final validation before updating state
      if (item.key !== currentItemKey) {
        console.warn('Item changed during server fetch, skipping state update')
        return
      }

      const result = updatedItem[0]
      //preformat the content in all languages in the result
      //only for text types
      for (const lang in result.translations) {
        result.translations[lang].content = controller.applyPreFormatters(
          result.translations[lang].content,
          item.type
        )
      }

      
      //pass on the saved item (but don't close dialog)
      // Call onSave callback to notify parent component of changes
      if (onSave) {
        onSave(result)
      }
      
      //clear the pending languages and freeze changes
      setPendingLanguages([])
      setPendingFreezeChanges({})
      setHasPendingFreezeChanges(false)

      // Update local translations with the saved result
      setTranslations(result.translations)

      // Update freeze states from saved result
      const updatedFreezeStates = {}
      for (const lang in result.translations) {
        if (result.translations[lang]) {
          // Always include the frozen array, even if it's empty
          updatedFreezeStates[lang] = result.translations[lang].frozen || []
        }
      }
      setFreezeStates(updatedFreezeStates)

      // Show success notification but don't close
      showNotification({
        message: "Template variables saved successfully!",
        className: "bg-admin_success text-white"
      })
    } catch (error) {
      console.error('Failed to save template variables:', error)
      showNotification({
        message: "Failed to save template variables",
        className: "bg-admin_error text-white"
      })
    }
  }

  const handleDelete = () => {
    showPromptDialog({
      title: "Delete Localization Key",
      message: "Are you sure you want to delete this localization key?",
      onConfirm: () => {
        controller.deleteLocalizationKeys([item.key])
        onClose()
      },
    })
  }

  /**
   * Check if the content is valid
   * @param {*} content
   * @returns
   */
  const isContentValid = (content) => {
    const value = content
    if (value === undefined || value === null) return false
    if (typeof value === "string" && value.trim() === "") return false
    if (Array.isArray(value) && value.length === 0) return false
    return true
  }

  // Define filter options
  const [filterOptions, setFilterOptions] = useState([
    { name: "Completed", count: 0, key: "completed_only" },
    { name: "Empty", count: 0, key: "empty_only" },
    { name: "All", count: 0, key: "all" },
  ])

  const filteredLanguagesMemo = useMemo(() => {
    return languages.filter((lang) => {
      const hasTranslation =
        translations && isContentValid(translations[lang.code]?.content)
      const matchesSearchTerm = lang.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase())

      // Check if the language is pending
      if (pendingLanguages.includes(lang.code)) return true

      if (filterOption === "completed_only" && !hasTranslation) return false
      if (filterOption === "empty_only" && hasTranslation) return false

      return matchesSearchTerm
    })
  }, [languages, searchTerm, filterOption, translations, pendingLanguages])
  
  useEffect(() => {
    setFilteredLanguages(filteredLanguagesMemo)
  }, [filteredLanguagesMemo])

  // Update the count of each filter option
  useEffect(() => {
    filterOptions.forEach((option) => {
      option.count = languages.filter((lang) => {
        // Check if the language is pending
        if (option.key === "all" && pendingLanguages.includes(lang.code))
          return true
        const hasTranslation =
          translations && isContentValid(translations[lang.code]?.content)
        if (option.key === "completed_only" && !hasTranslation) return false
        if (option.key === "empty_only" && hasTranslation) return false
        return true
      }).length
    })
  }, [
    controller.selectedAutoTranslateOption,
    translations,
    filteredLanguages,
    pendingLanguages,
    selectedLang,
  ])

  //start the auto translate on options change
  useEffect(() => {
    if (!controller.selectedAutoTranslateOption) return
    if (!selectedLang || !primaryLanguage) return // Prevent translation if languages not set
    //do the auto translation
    controller
      .autoTranslate(
        item.key,
        primaryLanguage,
        selectedLang,
        controller.selectedAutoTranslateOption
      )
      .then(async (response) => {
        // controller.autoTranslate already returns a pre-formatted item; refetch as a fallback.
        let translatedItem = response
        if (!translatedItem || !translatedItem.translations) {
          const updatedItem = await controller.getLocalizationKey(item.key)
          if (!updatedItem || !updatedItem[0] || !updatedItem[0].translations) return
          translatedItem = updatedItem[0]
          for (const lang in translatedItem.translations) {
            translatedItem.translations[lang].content = controller.applyPreFormatters(
              translatedItem.translations[lang].content,
              item.type
            )
          }
        }

        // Deep clone so React detects a new reference for every nested translation.
        const freshTranslations = JSON.parse(JSON.stringify(translatedItem.translations))

        // Update translations with the fetched data
        setTranslations(freshTranslations)

        // Update freeze states from the fetched data
        const updatedFreezeStates = {}
        for (const lang in freshTranslations) {
          updatedFreezeStates[lang] = freshTranslations[lang]?.frozen || []
        }
        setFreezeStates(updatedFreezeStates)

        // Publish PubSub event with the fresh translations
        PubSub.publish('localization.auto-translate-completed', {
          localizationKey: item.key,
          translations: freshTranslations,
          selectedLang,
          primaryLanguage
        })
      })
      .catch((error) => {
        console.error('Auto-translation error:', error)
        // Clear the auto translate option on error to prevent retries
        controller.setSelectedAutoTranslateOption("")
      })
  }, [controller.selectedAutoTranslateOption])

  // Save all pending freeze changes - batch by language to avoid race conditions
  const savePendingFreezeChanges = async () => {
    // Group changes by language to avoid multiple concurrent updates to the same language
    const changesByLanguage = {}
    
    for (const [changeKey, freezeChange] of Object.entries(pendingFreezeChanges)) {
      const lang = freezeChange.languageCode
      if (!changesByLanguage[lang]) {
        changesByLanguage[lang] = []
      }
      changesByLanguage[lang].push(freezeChange)
    }
    
    // Process each language sequentially to avoid race conditions
    for (const [language, changes] of Object.entries(changesByLanguage)) {
      // Get the current frozen array for this language
      const currentTranslation = translations[language] || {}
      let currentFrozenArray = [...(currentTranslation.frozen || [])]
      
      
      // Apply all changes for this language to build the final frozen array
      // We need to apply changes in the order they were made to get the correct final state
      for (const change of changes) {
        const fieldKey = change.fieldKey
        const frozen = change.frozen
        
        
        if (fieldKey === null || fieldKey === "*") {
          // Language-level freeze/unfreeze
          currentFrozenArray = frozen ? ["*"] : []
        } else {
          // Field-specific freeze/unfreeze
          if (currentFrozenArray.includes("*")) {
            // Transitioning from language freeze to field-specific
            if (frozen) {
              // Field should remain frozen (already frozen via "*") - keep "*"
              currentFrozenArray = ["*"]
            } else {
              // Field should be unfrozen - get all fields and freeze the others
              const allFields = []
              if (currentTranslation.content && Array.isArray(currentTranslation.content)) {
                currentTranslation.content.forEach(item => {
                  if (item && item.key) {
                    allFields.push(item.key)
                  }
                })
              }
              // Freeze all fields EXCEPT the one being unfrozen
              currentFrozenArray = allFields.filter(f => f !== fieldKey)
            }
          } else {
            // Normal field-specific toggle
            if (frozen) {
              // Add field to frozen list
              if (!currentFrozenArray.includes(fieldKey)) {
                currentFrozenArray.push(fieldKey)
              }
            } else {
              // Remove field from frozen list
              currentFrozenArray = currentFrozenArray.filter(f => f !== fieldKey)
            }
          }
        }
        
      }
      
      // Make a single API call per language with the final frozen array
      // We'll use the first change's fieldKey as a representative, but the API will use our computed array
      const representativeChange = changes[0]
      
      // Since the API doesn't support setting the entire array directly, we need to simulate it
      // by making individual calls in the correct order
      
      // First, clear all existing frozen fields if we have specific changes
      if (currentFrozenArray.length === 0) {
        // Unfreeze everything - API call with "*" and frozen=false
        await FreezeTranslation({
          key: item.key,
          language: language,
          fieldKey: "*",
          frozen: false
        })
      } else if (currentFrozenArray.includes("*")) {
        // Freeze everything
        await FreezeTranslation({
          key: item.key,
          language: language,
          fieldKey: "*",
          frozen: true
        })
      } else {
        // First unfreeze everything, then freeze specific fields
        await FreezeTranslation({
          key: item.key,
          language: language,
          fieldKey: "*",
          frozen: false
        })
        
        // Then freeze each specific field
        for (const fieldKey of currentFrozenArray) {
          await FreezeTranslation({
            key: item.key,
            language: language,
            fieldKey: fieldKey,
            frozen: true
          })
        }
      }
    }
  }

  // Handle freeze toggle for regular translations - now with pending state
  const handleFreezeToggle = (languageCode, fieldKey = null) => {
    if (!item) return
    
    const currentTranslation = translations[languageCode] || {}
    
    // Get the current frozen array - prioritize server state from translations over local freeze states
    // This ensures we always work with the actual current state from the database
    const serverFrozenArray = currentTranslation.frozen || []
    const localFrozenArray = freezeStates[languageCode]
    
    // Use server state as baseline, only override with local state if we have pending changes
    const currentFrozenArray = localFrozenArray !== undefined 
      ? localFrozenArray 
      : serverFrozenArray
    
    
    let newFrozenArray
    let newFrozenState
    
    if (fieldKey === null || fieldKey === "*") {
      // Language-level freeze toggle (entire translation)
      const isCurrentlyLanguageFrozen = currentFrozenArray.includes("*")
      
      if (isCurrentlyLanguageFrozen) {
        // Language is frozen with "*", clicking toggles it OFF - remove everything
        newFrozenState = false
        newFrozenArray = []
      } else {
        // Language is not frozen with "*", clicking toggles it ON - set "*"
        newFrozenState = true
        newFrozenArray = ["*"]
      }
      
    } else {
      // Field-specific freeze toggle
      const isCurrentlyFrozen = currentFrozenArray.includes("*") || currentFrozenArray.includes(fieldKey)
      newFrozenState = !isCurrentlyFrozen
      
      if (currentFrozenArray.includes("*")) {
        // Currently language-level frozen, user clicks a field to unfreeze it
        // Need to transition from "*" to explicit field list with this field unfrozen
        
        // Get all fields from the content to build the explicit list
        const allFields = []
        if (currentTranslation.content && Array.isArray(currentTranslation.content)) {
          currentTranslation.content.forEach(item => {
            if (item && item.key) {
              allFields.push(item.key)
            }
          })
        }
        
        if (newFrozenState) {
          // Field should remain frozen (this shouldn't happen since it's already frozen via "*")
          // But if it does, keep the "*" 
          newFrozenArray = ["*"]
        } else {
          // Field should be unfrozen - freeze all OTHER fields explicitly
          newFrozenArray = allFields.filter(f => f !== fieldKey)
        }
        
      } else {
        // Normal field-specific toggle (no "*" involved)
        if (newFrozenState) {
          // Add field to frozen list
          newFrozenArray = [...currentFrozenArray.filter(f => f !== fieldKey), fieldKey]
        } else {
          // Remove field from frozen list
          newFrozenArray = currentFrozenArray.filter(f => f !== fieldKey)
        }
      }
    }
    
    
    // Update local freeze states immediately for visual feedback
    setFreezeStates(prev => ({
      ...prev,
      [languageCode]: newFrozenArray
    }))
    
    // Update translations frozen array immediately for visual feedback
    setTranslations(prev => ({
      ...prev,
      [languageCode]: {
        ...prev[languageCode],
        frozen: newFrozenArray
      }
    }))
    
    // Track this change as pending
    const changeKey = `${languageCode}${fieldKey ? `_${fieldKey}` : ''}`
    setPendingFreezeChanges(prev => ({
      ...prev,
      [changeKey]: {
        languageCode,
        fieldKey,
        frozen: newFrozenState
      }
    }))
    
    setHasPendingFreezeChanges(true)
  }

  return (
    <Modal
      variant="admin_fullScreen"
      {...(zIndex != null ? { zIndex } : {})}
      title={
        <div className="flex items-center justify-between w-full pr-12">
          <span>Localization Key</span>
          <span className="text-admin_text_secondary text-base font-normal">
            key: <span className="font-semibold text-admin_text">{item?.key}</span>
          </span>
        </div>
      }
      isOpen={isOpen}
      onClose={() => {
        onClose()
      }}
      closeOnEscape={true}
      footerContent={
        <div className="flex justify-end items-center gap-3 mr-8 mb-6">
          {/* Freeze status indicator - next to delete button on its left */}
          {selectedLang && translations && translations[selectedLang] && (() => {
            const currentTranslation = translations[selectedLang]
            const effectiveFrozenArray = freezeStates[selectedLang] !== undefined 
              ? freezeStates[selectedLang] 
              : currentTranslation.frozen || []
            
            // Check if translation has any freeze status
            if (effectiveFrozenArray && effectiveFrozenArray.length > 0) {
              const freezeState = getFreezeState(currentTranslation, effectiveFrozenArray)
              const isLanguageFrozen = effectiveFrozenArray.includes("*")
              
              return (
                <div className="flex items-center gap-2 text-admin_accent_2">
                  <SnowflakeIcon state={freezeState} className="!p-0.5" />
                  <span className="text-sm">
                    {isLanguageFrozen 
                      ? "This translation will not be affected by auto-translation"
                      : item?.type === 'object' 
                        ? "Some fields will not be affected by auto-translation"
                        : "This translation will not be affected by auto-translation"
                    }
                  </span>
                </div>
              )
            }
            return null
          })()}

          <Button
            variant="admin_error"
            onClick={handleDelete}
          >
            Delete
          </Button>

          <Button
            variant="admin_secondary"
            onClick={() => {
              onClose()
            }}
          >
            Close
          </Button>

          <Button
            variant="admin_primary"
            onClick={handleSave}
            className={hasPendingFreezeChanges || pendingLanguages.length > 0 ? "ring-2 ring-admin_progress" : ""}
          >
            {hasPendingFreezeChanges || pendingLanguages.length > 0 ? "Save Changes" : "Save"}
          </Button>
        </div>
      }
    >

      <div className="flex h-[calc(100vh-170px)] overflow-hidden">
        {/* Left side */}
        <div className="flex flex-col w-[30%] xl:w-[25%] h-full overflow-y-auto">
          {/* Search and Filter Controls */}
          <div className="flex justify-between gap-2 mb-4">
            {/* Search Input */}
            <Input
              variant="admin_compact"
              placeholder="Search languages..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-[38px]"
            />
            
            {/* DropDown to filter languages */}
            <div className="w-[150px]">
              <DropDown
                isMenu={true}
                placeholder="Filter"
                options={filterOptions}
                onChange={(item) => setFilterOption(item.key)}
                value={filterOptions.find(
                  (option) => option.key === filterOption
                )}
                menuPosition="bottom"
                onItemRender={(item) => item.name + ` - ${item.count}`}
                className="w-full min-w-[150px]"
              />
            </div>
          </div>

          {/* Filtered language list */}
          <div className="flex flex-col w-full flex-1 overflow-y-auto bg-admin_secondary/30 rounded-xl">
            {filteredLanguages.map((lang) => (
              <div
                key={lang.code}
                className={`text-left text-admin_text flex items-center justify-between  p-4 ${
                  selectedLang === lang.code
                    ? "text-white bg-admin_primary"
                    : "hover:bg-admin_secondary/40 hover:text-admin_text transition-colors"
                }`}
              >
                <button
                  className={`flex-1 text-left flex items-center gap-2 ${
                    selectedLang === lang.code ? "text-admin_text_inv" : ""
                  }`}
                  onClick={() => handleLanguageSelect(lang.code)}
                >
                  {/* Status indicator with reserved space */}
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    {pendingLanguages.includes(lang.code) ? (
                      <div className="w-2 h-2 bg-admin_warning rounded-full"></div>
                    ) : !pendingLanguages.includes(lang.code) &&
                      translations &&
                      isContentValid(translations[lang.code]?.content) ? (
                        <span className={`text-admin_primary ${
                    selectedLang === lang.code ? "text-admin_text_inv" : "text-admin_primary"
                  }`}>&#10003;</span>
                      ) : null}
                  </div>
                  
                  <span>{lang.name}</span>
                </button>
                
                <div className="flex flex-col items-center gap-1">
                  {/* Freeze control - only show for object types or when translation exists */}
                  {(item?.type === 'object' || (translations && translations[lang.code])) && (() => {
                    // Use pending state first, then local state, then server state
                    const currentTranslation = translations[lang.code] || {}
                    const pendingFrozenArray = freezeStates[lang.code]
                    const serverFrozenArray = currentTranslation.frozen
                    
                    // Prioritize pending state over server state
                    const effectiveFrozenArray = pendingFrozenArray !== undefined ? pendingFrozenArray : serverFrozenArray
                    const freezeState = getFreezeState(currentTranslation, effectiveFrozenArray)
                    const freezeTitle = getFreezeTitle(freezeState)
                    
                    const languageName = controller.getLanguageName(lang.code)
                    const isLanguageFrozen = effectiveFrozenArray && effectiveFrozenArray.includes("*")
                    const tooltipContent = isLanguageFrozen
                      ? "Allow auto-translation to modify this language"
                      : "Prevent auto-translation from modifying this language"
                    
                    return (
                      <Tooltip
                        content={tooltipContent}
                        variant="admin"
                      >
                        <SnowflakeIcon
                          state={freezeState}
                          onClick={(e) => {
                            e.stopPropagation()
                            // Language-level freeze toggle works for all types
                            // This will toggle between ["*"] and [] to freeze/unfreeze the entire translation
                            handleFreezeToggle(lang.code, "*")
                          }}
                        />
                      </Tooltip>
                    )
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Translation Content */}
        <div className="w-[70%] xl:w-[75%] flex flex-col p-4 pt-0 overflow-y-auto">
          {(() => {
            if (EditorComponent && item && translations && selectedLang && Object.keys(translations).length > 0) {
              return (
                <EditorComponent
                  key={`${item.key}-${selectedLang}`}
                  title="Hello"
                  localizationKey={item?.key}
                  parentObject={parentObject}
                  controller={controller}
                  selectedLang={selectedLang}
                  translations={translations}
                  textAreaRef={textAreaRef}
                  handleContentChange={handleContentChange}
                  primaryLanguage={primaryLanguage}
                  freezeStates={freezeStates}
                  onFreezeToggle={handleFreezeToggle}
                  pendingFreezeChanges={pendingFreezeChanges}
                  item={item}
                />
              )
            }
            // Show loading spinner while data is being fetched
            return (
              <div className="flex items-center justify-center h-full">
                <Spinner variant="loading" />
              </div>
            )
          })()}


        </div>
      </div>
    </Modal>
  )
}
